import * as TCurrency from "../models/TCurrency";

// Whether a listing's investor-facing numbers may be shown to a buyer. Amounts are in POUNDS.
// Used by the listing page, the viewing-confirmed email and the investor alert — three copies
// would drift, and one surface would publish a figure another had already refused.

// Outside this band the rent is bad data, not a remarkable property: a live listing sits at
// £50,000 asking with £850pcm entered (20.4%). The floor catches an annual figure typed into
// the monthly field.
export const PLAUSIBLE_GROSS_YIELD_MIN_PERCENT = 2;
export const PLAUSIBLE_GROSS_YIELD_MAX_PERCENT = 15;

// Narrower than the plausibility band — believable is not the same as worth claiming.
export const STRONG_GROSS_YIELD_MIN_PERCENT = 5;

export type TListingInvestorFigures = {
    price: number | null;
    estimated_rental_price_pcm: number | null;
    renovation_cost: number | null;
    estimated_resale_price: number | null;
    renovation_estimate_verified: boolean;
    no_renovation_potential: boolean;
};

export const grossYieldPercent = (listing: TListingInvestorFigures): number | null =>
    listing.estimated_rental_price_pcm !== null
    && listing.estimated_rental_price_pcm > 0
    && listing.price !== null
    && listing.price > 0
        ? ((listing.estimated_rental_price_pcm * 12) / listing.price) * 100
        : null;

// Gated on the yield, not the rent: an ordinary-looking rent can still be wrong for the
// property. No asking price means nothing to check against, so the rent is withheld.
export const canPublishPotentialRent = (listing: TListingInvestorFigures): boolean => {
    const yieldPercent = grossYieldPercent(listing);

    return yieldPercent !== null
        && yieldPercent >= PLAUSIBLE_GROSS_YIELD_MIN_PERCENT
        && yieldPercent <= PLAUSIBLE_GROSS_YIELD_MAX_PERCENT;
};

// Computed from the yield, which is never printed — a percentage reads as an underwritten return.
export const hasStrongRentalReturn = (listing: TListingInvestorFigures): boolean => {
    const yieldPercent = grossYieldPercent(listing);

    return canPublishPotentialRent(listing)
        && yieldPercent !== null
        && yieldPercent >= STRONG_GROSS_YIELD_MIN_PERCENT;
};

// `no_renovation_potential` WINS over a sign-off. Both can be set on one row and a schema CHECK
// forbidding that was proposed on #3981 and rejected: UpdateListingFullController writes
// through convertFormToTuplesOfChangedValues, which emits only CHANGED columns, so ticking the
// flag on an already-signed-off listing would be a single-column UPDATE that violates the
// constraint and 500s the whole form save. The ambiguity is tolerated in the schema and
// resolved here instead. Both amounts must be positive — two live listings store a cost of 0
// beside a six-figure uplift.
export const canPublishRenovation = (listing: TListingInvestorFigures): boolean =>
    !listing.no_renovation_potential
    && listing.renovation_estimate_verified
    && listing.renovation_cost !== null
    && listing.renovation_cost > 0
    && listing.estimated_resale_price !== null
    && listing.estimated_resale_price > 0
    && listing.price !== null
    && listing.price > 0
    // The uplift has to be positive. Without this a buyer is shown works that lose money.
    && listing.estimated_resale_price > listing.price + listing.renovation_cost;

// Null wherever canPublishRenovation refuses, rather than computing from a half-set row.
// What a surface is allowed to print, after the gate. A refused figure is null here rather
// than hidden later, so no consumer can print one by reaching past the gate.
export type TPublishedInvestorFigures = {
    potential_rent_pcm: number | null;
    has_strong_rental_return: boolean;
    renovation_cost: number | null;
    estimated_resale_price: number | null;
    renovation_uplift: number | null;
};

export const NOTHING_PUBLISHED: TPublishedInvestorFigures = {
    potential_rent_pcm: null,
    has_strong_rental_return: false,
    renovation_cost: null,
    estimated_resale_price: null,
    renovation_uplift: null,
};

export const INVESTOR_FIGURES_CAVEAT =
    "These are our estimates, not a valuation or a promise. Please check the numbers yourself before you buy.";

export const publishedInvestorFigures = (listing: TListingInvestorFigures): TPublishedInvestorFigures => {
    const renovationPublishable = canPublishRenovation(listing);

    return {
        potential_rent_pcm: canPublishPotentialRent(listing) ? listing.estimated_rental_price_pcm : null,
        has_strong_rental_return: hasStrongRentalReturn(listing),
        renovation_cost: renovationPublishable ? listing.renovation_cost : null,
        estimated_resale_price: renovationPublishable ? listing.estimated_resale_price : null,
        renovation_uplift: renovationUpliftPounds(listing),
    };
};

// The rows themselves, so the page and the email cannot disagree about which figures a
// buyer sees or how they are worded. Each surface still owns its own markup.
export const investorFactRows = (figures: TPublishedInvestorFigures): Array<{ label: string; value: string }> => [
    ...(figures.potential_rent_pcm === null
        ? []
        : [{ label: "Rent you could get", value: `${TCurrency.fromNumber(figures.potential_rent_pcm)} a month` }]),
    // All three renovation numbers stand or fall together — a cost with no resale beside it
    // is a bill with nothing to weigh it against.
    ...(figures.renovation_uplift === null
        || figures.renovation_cost === null
        || figures.estimated_resale_price === null
        ? []
        : [
            { label: "Work needed", value: TCurrency.fromNumber(figures.renovation_cost) },
            { label: "Worth after the work", value: TCurrency.fromNumber(figures.estimated_resale_price) },
        ]),
];

export const renovationUpliftPounds = (listing: TListingInvestorFigures): number | null =>
    canPublishRenovation(listing)
    && listing.estimated_resale_price !== null
    && listing.renovation_cost !== null
    && listing.price !== null
        ? listing.estimated_resale_price - listing.price - listing.renovation_cost
        : null;
